/**
 * Safe rich-text helpers for clinical notes.
 *
 * The editor stores a constrained JSON document and the renderer creates React
 * elements from that structure; note content is never interpreted as HTML.
 */

import type {
  RichTextBlock,
  RichTextDocument,
  RichTextMark,
  RichTextSpan,
} from "../types/notes";

/** Create an empty document suitable for a new note or reply. */
export function createEmptyRichText(): RichTextDocument {
  return {
    schema_version: 1,
    content: {
      blocks: [{ type: "paragraph", content: [{ text: "" }] }],
    },
    plain_text: "",
  };
}

/**
 * Convert plain text into the constrained rich-text contract.
 *
 * @param value - Newline-delimited editor text
 * @param marks - Marks applied to every generated span
 * @returns Versioned rich-text document
 */
export function plainTextToRichText(
  value: string,
  marks: RichTextMark[] = [],
): RichTextDocument {
  const lines = value.split("\n");
  return {
    schema_version: 1,
    content: {
      blocks: lines.map((line): RichTextBlock => ({
        type: "paragraph",
        content: [{ text: line, ...(marks.length ? { marks } : {}) }],
      })),
    },
    plain_text: value,
  };
}

/**
 * Extract searchable/display text from a structured note body.
 *
 * @param document - Structured rich-text document
 * @returns Newline-delimited plain text
 */
export function richTextToPlainText(document: RichTextDocument): string {
  return document.content.blocks
    .map((block) => block.content.map((span) => span.text).join(""))
    .join("\n");
}

/**
 * Apply or remove a mark from all current text spans.
 *
 * This deliberately avoids deprecated browser editing commands and produces a
 * predictable JSON payload across browsers.
 *
 * @param document - Current editor document
 * @param mark - Mark to toggle
 * @returns Updated immutable rich-text document
 */
export function toggleDocumentMark(
  document: RichTextDocument,
  mark: RichTextMark,
): RichTextDocument {
  const everySpanMarked = document.content.blocks
    .flatMap((block) => block.content)
    .every((span) => span.marks?.includes(mark));
  return {
    ...document,
    content: {
      blocks: document.content.blocks.map((block) => ({
        ...block,
        content: block.content.map(
          (span): RichTextSpan => ({
            ...span,
            marks: everySpanMarked
              ? span.marks?.filter((item) => item !== mark)
              : Array.from(new Set([...(span.marks ?? []), mark])),
          }),
        ),
      })),
    },
  };
}
